import {
  Grid,
  MenuItem,
  Box,
  FormControl,
  FormLabel,
  Stack,
  Typography,
  Button,
  Avatar,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { Country, State } from "country-state-city";
import { phone } from "phone";
import { OrganisationLineItem } from "@interfaces/organisation";
import { validateABN } from "@utils/abn";
import { useRef, useState } from "react";
import { CircularProgressWithLabel } from "@stories/molecules/CircularProgressWithLabel/CircularProgressWithLabel";
import { uploadImage } from "@hooks/utils/useUpload";
import { StyledButton } from "@stories/atoms/StyledButton/StyledButton";
import { StyledTextField } from "@stories/atoms/StyledTextField/StyledTextField";
import { validatePostCode } from "@utils/location";
import UploadTwoToneIcon from "@mui/icons-material/UploadTwoTone";
import { ToastOptions, toast } from "react-toastify";

interface CompanyFormProps {
  organisation: OrganisationLineItem;
  handleNext: () => void;
  setOrganisation: (organisation: OrganisationLineItem) => void;
}

export const CompanyForm = ({
  organisation,
  setOrganisation,
  handleNext,
}: CompanyFormProps) => {
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [tempLogoUrl, setTempLogoUrl] = useState(organisation.organisationLogo);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const validationSchema = yup.object({
    organisationName: yup.string().required("Organisation name is required"),
    tradingName: yup.string().required("Trading name is required"),
    abn: yup
      .string()
      .required("ABN is required")
      .test(
        "Valid ABN",
        "Not a valid ABN - i.e. 53 004 085 616",
        function (item) {
          return validateABN(item);
        }
      ),
    representativeName: yup
      .string()
      .required("Representative name is required"),
    representativeEmail: yup
      .string()
      .email()
      .required("Representative email is required"),
    phoneNumber: yup
      .string()
      .required("Phone number is required")
      .test("Valid Phone", "Phone not valid for country", function (item) {
        const res = phone(item, {
          country: this.parent.country,
          validateMobilePrefix: false,
        });
        return res.isValid;
      }),
    organisationLogoSrc: yup.string().required("Organisation logo is required"),
    addressLine1: yup.string().required("Address Line 1 is required"),
    addressLine2: yup.string(),
    country: yup.string().required("Country is required"),
    state: yup.string().required("State is required"),
    city: yup.string().required("City is required"),
    postCode: yup
      .string()
      .required("Post code is required")
      .test(
        "Valid for country",
        "Not a valid post code for country",
        function (item) {
          return validatePostCode(this.parent.country, item);
        }
      ),
  });
  const formik = useFormik({
    initialValues: {
      id: organisation.id,
      organisationName: organisation.organisationName,
      tradingName: organisation.tradingName,
      abn: organisation.abn,
      representativeName: organisation.representativeName,
      representativeEmail: organisation.representativeEmail,
      phoneNumber: organisation.phoneNumber,
      addressLine1: organisation.addressLine1,
      addressLine2: organisation.addressLine2,
      city: organisation.city,
      postCode: organisation.postCode,
      country: organisation.country,
      state: organisation.state,
      organisationLogoSrc: organisation.organisationLogoSrc,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setOrganisation({
        ...organisation,
        organisationName: values.organisationName,
        tradingName: values.tradingName,
        abn: values.abn,
        representativeName: values.representativeName,
        representativeEmail: values.representativeEmail,
        phoneNumber: values.phoneNumber,
        addressLine1: values.addressLine1,
        addressLine2: values.addressLine2,
        city: values.city,
        postCode: values.postCode,
        country: values.country,
        state: values.state,
        organisationLogoSrc: values.organisationLogoSrc,
        organisationLogo: tempLogoUrl,
      });
      handleNext();
    },
  });

  const onFileInput = () => {
    if (fileInput.current) {
      fileInput.current.click();
    }
  };

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadProgress(0);
    if (e.currentTarget?.files && e.currentTarget?.files.length > 0) {
      const file = e.currentTarget?.files[0] || null;
      if (file) {
        setIsUploading(true);

        const fileKey = `logos/${organisation.id}/${file.name}`;
        await uploadImage(
          fileKey,
          file,
          (progress: any) => {
            const total = (progress.loaded / progress.total) * 100;
            setUploadProgress(total);
            if (total === 100) {
              toast("Upload complete!", {
                type: "success",
              } as ToastOptions);
            }
          },
          (error: any) => {
            setUploadProgress(0);
            console.log(error);
            toast("Upload failed - something went wrong!", {
              type: "error",
            } as ToastOptions);
            setIsUploading(false);
          }
        );

        formik.setFieldValue("organisationLogoSrc", fileKey);

        const urlToImage = URL.createObjectURL(file);
        formik.setFieldValue("organisationLogo", urlToImage);
        setTempLogoUrl(urlToImage);
        setIsUploading(false);
      }
    }
  };

  const countries = Country.getAllCountries();
  const states = State.getStatesOfCountry(formik.values.country);
  return (
    <Box>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={6} sm={6}>
            <StyledTextField
              id="organisationName"
              name="organisationName"
              label="Organisation Name"
              fullWidth
              margin="dense"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.organisationName}
              error={
                formik.touched.organisationName &&
                Boolean(formik.errors.organisationName)
              }
              helperText={
                formik.touched.organisationName
                  ? formik.errors.organisationName
                  : ""
              }
            />
          </Grid>
          <Grid item xs={6} sm={6}>
            <StyledTextField
              id="tradingName"
              name="tradingName"
              label="Trading Name"
              fullWidth
              margin="dense"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.tradingName}
              error={
                formik.touched.tradingName && Boolean(formik.errors.tradingName)
              }
              helperText={
                formik.touched.tradingName ? formik.errors.tradingName : ""
              }
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <StyledTextField
              id="abn"
              name="abn"
              label="ABN"
              fullWidth
              margin="dense"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.abn}
              error={formik.touched.abn && Boolean(formik.errors.abn)}
              helperText={formik.touched.abn ? formik.errors.abn : ""}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StyledTextField
              id="representativeName"
              name="representativeName"
              label="Representative Name"
              fullWidth
              margin="dense"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.representativeName}
              error={
                formik.touched.representativeName &&
                Boolean(formik.errors.representativeName)
              }
              helperText={
                formik.touched.representativeName
                  ? formik.errors.representativeName
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StyledTextField
              id="representativeEmail"
              name="representativeEmail"
              label="Representative Email"
              fullWidth
              margin="dense"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.representativeEmail}
              error={
                formik.touched.representativeEmail &&
                Boolean(formik.errors.representativeEmail)
              }
              helperText={
                formik.touched.representativeEmail
                  ? formik.errors.representativeEmail
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StyledTextField
              id="addressLine1"
              name="addressLine1"
              label="Address Line 1"
              fullWidth
              margin="dense"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.addressLine1}
              error={
                formik.touched.addressLine1 &&
                Boolean(formik.errors.addressLine1)
              }
              helperText={
                formik.touched.addressLine1 ? formik.errors.addressLine1 : ""
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StyledTextField
              id="addressLine2"
              name="addressLine2"
              label="Address Line 2"
              fullWidth
              margin="dense"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.addressLine2}
              error={
                formik.touched.addressLine2 &&
                Boolean(formik.errors.addressLine2)
              }
              helperText={
                formik.touched.addressLine2 ? formik.errors.addressLine2 : ""
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <StyledTextField
                select
                id="country"
                name="country"
                margin="dense"
                value={formik.values.country}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.country && Boolean(formik.errors.country)}
                helperText={formik.touched.country ? formik.errors.country : ""}
                label="Country"
              >
                {countries.map((c, index) => (
                  <MenuItem key={c.isoCode} value={c.isoCode}>
                    {c.name}
                  </MenuItem>
                ))}
              </StyledTextField>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <StyledTextField
                select
                id="state"
                name="state"
                margin="dense"
                value={formik.values.state}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.state && Boolean(formik.errors.state)}
                helperText={formik.touched.state ? formik.errors.state : ""}
                label="State"
              >
                {states.map((s, index) => (
                  <MenuItem key={`${s.isoCode}-${index}`} value={s.isoCode}>
                    {s.name}
                  </MenuItem>
                ))}
              </StyledTextField>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <StyledTextField
                id="city"
                name="city"
                margin="dense"
                value={formik.values.city}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.city && Boolean(formik.errors.city)}
                helperText={formik.touched.city ? formik.errors.city : ""}
                label="City"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <StyledTextField
              id="postCode"
              name="postCode"
              label="Postcode"
              fullWidth
              margin="dense"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.postCode}
              error={formik.touched.postCode && Boolean(formik.errors.postCode)}
              helperText={formik.touched.postCode ? formik.errors.postCode : ""}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StyledTextField
              id="phoneNumberi"
              name="phoneNumber"
              label="Phone Number"
              fullWidth
              margin="dense"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.phoneNumber}
              error={
                formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)
              }
              helperText={
                formik.touched.phoneNumber ? formik.errors.phoneNumber : ""
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} />
          <Grid
            item
            xs={6}
            sm={6}
            sx={{
              display: "flex !important",
              justifyContent: "left !important;",
            }}
          >
            <Stack direction={"row"} sx={{ flexGrow: 1 }} spacing={2}>
              <FormLabel component="legend">Organisation Logo</FormLabel>
              <Box sx={{ flexGrow: 1 }}>
                {isUploading ? (
                  <CircularProgressWithLabel value={uploadProgress} />
                ) : (
                  <Grid item xs={12}>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item>
                        <Avatar
                          alt="User 3"
                          src={tempLogoUrl}
                          sx={{ width: 64, height: 64 }}
                        />
                      </Grid>
                      <Grid item xs zeroMinWidth>
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <label htmlFor="containedButtonFile">
                              <input
                                ref={fileInput}
                                hidden
                                accept="image/*"
                                type="file"
                                style={{
                                  opacity: 0,
                                  position: "fixed",
                                  zIndex: 1,
                                  padding: 0.5,
                                  cursor: "pointer",
                                  width: "100px",
                                  height: "100px",
                                }}
                                onChange={onUpload}
                              />
                              <Button
                                variant="outlined"
                                size="small"
                                disabled={isUploading}
                                startIcon={<UploadTwoToneIcon />}
                                onClick={onFileInput}
                              >
                                Upload
                              </Button>
                            </label>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption">
                              Image size should no more than 10Mb Max.
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Stack>
          </Grid>
          <Grid
            item
            xs={6}
            sm={6}
            sx={{
              display: "flex !important",
              justifyContent: "right !important;",
            }}
          ></Grid>

          <Grid
            item
            xs={6}
            sm={6}
            sx={{
              display: "flex !important",
              justifyContent: "left !important;",
            }}
          ></Grid>
          <Grid
            item
            xs={6}
            sm={6}
            sx={{
              display: "flex !important",
              justifyContent: "right !important;",
            }}
          >
            <StyledButton variant="contained" type="submit">
              Next
            </StyledButton>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};
